import { Button, CircularProgress, Stack, Box, Typography, Paper, Container, Chip, Divider } from "@mui/material";
import { Settings as SettingsIcon, Save as SaveIcon, Category as CategoryIcon, Business as BusinessIcon } from "@mui/icons-material";
import { type Organization } from "@entities";
import { type FC, useEffect } from "react";
import { useAlert } from "../../contexts/AlertProvider";
import { useOrganization } from "../../contexts/OrganizationProvider";
import FormProvider, {
  useForm,
  useFormConfig,
} from "../../helpers/forms/FormProvider";
import { provide } from "../../helpers/wrap-and-provide";
import "./Settings.scss";
import UpdateOrganizationInputs from "./UpdateOrganizationInputs";
import { UploadImage } from "../../components/form-components/image-uploader/ImageUploader";
import ListEdit from "./ListEdit";
import { FormTextField } from "../../components/form-components/form-text-field/FormTextField";
import { OrganizationBackgroundGallerySettings } from "./OrganizationBackgroundGallerySettings";

export interface NameWithCategory { id: string; name: string; category: string; }

export interface SettingsFormValues
  extends Partial<Organization> {
  name: string;
  profileImageId: string;
  setupDescription: string;
  positionsWithCategory: NameWithCategory[];
  notificationTemplates?: {
    newVolunteer?: string;
    newGroup?: string;
    groupPending?: string;
    groupApproved?: string;
    groupRejected?: string;
  };
}

const DEFAULT_INITIAL_VALUES = {
  name: "",
  profileImageId: "",
  setupDescription: "",
};

const SettingsPage: FC = () => {
  const { currOrganization, updateCurrOrganization } = useOrganization();
  const { setValues, setFieldValue, values } = useForm<SettingsFormValues>();
  const { setAlertText, setSeverity, setTime } = useAlert();

  // מרכז את השקיפות של כל החלקים
  const HEADER_OPACITY = 0.85;

  useEffect(() => {
    if (!currOrganization) return;
    setValues({
      name: currOrganization.name,
      profileImageId: currOrganization.organizationImageId,
      setupDescription: currOrganization.setupDescription || "",
      positionsWithCategory: currOrganization.positionsWithCategory || [],
    });
  }, [currOrganization]);

  useFormConfig<SettingsFormValues>(
    (form) => {
      form.onSubmit = async (values) => {
        try {
          await updateCurrOrganization({
            ...values,
          });
        } catch {
          setSeverity("warning");
          setTime(2000);
          setAlertText("משהו קרה אנא נסו שנית");
        } finally {
          setSeverity("success");
          setTime(2000);
          setAlertText("הארגון עודכן בהצלחה");
        }
      };
    },
    [currOrganization],
  );

  if (!currOrganization) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <CircularProgress size={60} sx={{ color: "white" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: "1rem",

        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Page Header */}
        <Box
          sx={{
            textAlign: "center",
            mb: 6,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "50%",
              p: 3,
              mb: 3,
              boxShadow: "0 8px 32px rgba(102, 126, 234, 0.4)",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "50%",
                zIndex: -1,
                opacity: 0.3,
                filter: "blur(8px)",
              },
            }}
          >
            <SettingsIcon sx={{ color: "white", fontSize: 36 }} />
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 2,
              fontSize: { xs: "2rem", md: "2.5rem" },
              letterSpacing: "-0.02em",
            }}
          >
            הגדרות הארגון
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#64748b",
              fontWeight: 500,
              maxWidth: "700px",
              mx: "auto",
              lineHeight: 1.7,
              fontSize: "1.1rem",
            }}
          >
            נהל את המידע הבסיסי של הארגון, תהליכי ההרשמה ותבניות ההתראות
          </Typography>
        </Box>

        {/* Main Content */}
        <Stack spacing={4}>
          {/* 1. Organization Basic Info Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid rgba(255, 255, 255, 0.2)",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, rgba(102, 126, 234, ${HEADER_OPACITY}) 0%, rgba(118, 75, 162, ${HEADER_OPACITY}) 100%)`,
                p: 3,
                color: "white",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  fontSize: "1.2rem",
                }}
              >
                <BusinessIcon sx={{ fontSize: 28 }} />
                מידע בסיסי על הארגון
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  mt: 1,
                  fontSize: "0.95rem",
                  fontWeight: 400,
                }}
              >
                עדכן את המידע הבסיסי של הארגון שיוצג למתנדבים
              </Typography>
            </Box>
            <Box sx={{ p: 4 }}>
              <UpdateOrganizationInputs />
            </Box>
          </Paper>

          {/* 2. Profile Image Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid rgba(255, 255, 255, 0.2)",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, rgba(76, 175, 80, ${HEADER_OPACITY}) 0%, rgba(69, 160, 73, ${HEADER_OPACITY}) 100%)`,
                p: 3,
                color: "white",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  fontSize: "1.2rem",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  📷
                </Box>
                תמונת פרופיל הארגון
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  mt: 1,
                  fontSize: "0.95rem",
                  fontWeight: 400,
                }}
              >
                תמונה שתוצג בראש עמוד הארגון ובחיפוש מתנדבים
              </Typography>
            </Box>
            <Box sx={{ p: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <UploadImage
                  imageId={values.profileImageId}
                  name="profileImageId"
                  handleImageChange={async (imageId) => {
                    setFieldValue("profileImageId", imageId);
                  }}
                />
              </Box>
            </Box>
          </Paper>

          <OrganizationBackgroundGallerySettings headerOpacity={HEADER_OPACITY} />

          {/* 3. Categories Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid rgba(255, 255, 255, 0.2)",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, rgba(255, 107, 107, ${HEADER_OPACITY}) 0%, rgba(238, 90, 36, ${HEADER_OPACITY}) 100%)`,
                p: 3,
                color: "white",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  fontSize: "1.2rem",
                }}
              >
                <CategoryIcon sx={{ fontSize: 28 }} />
                קטגוריות ותפקידים
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  mt: 1,
                  fontSize: "0.95rem",
                  fontWeight: 400,
                }}
              >
                נהל את הקטגוריות והתפקידים בארגון
              </Typography>
            </Box>
            <Box sx={{ p: 4, }}>
              <ListEdit list="positionsWithCategory" listName="קטגוריות התנדבות" />
            </Box>
          </Paper>

        </Stack>

        {/* Save Button Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 6,
            mb: 4,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              px: 8,
              py: 2.5,
              fontSize: "1.1rem",
              fontWeight: 700,
              borderRadius: 4,
              textTransform: "none",
              boxShadow: "0 8px 32px rgba(102, 126, 234, 0.4)",
              transition: "all 0.3s ease",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                transition: "left 0.5s",
              },
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 12px 40px rgba(102, 126, 234, 0.6)",
                "&::before": {
                  left: "100%",
                },
              },
              "&:active": {
                transform: "translateY(-1px)",
              },
            }}
          >
            שמירת השינויים
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default provide([
  FormProvider,
  {
    initialValues: DEFAULT_INITIAL_VALUES,
    onSubmit: () => { },
    className: "register-form settings-page",
  },
])(SettingsPage);
